import { Component, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DonateBtnComponent } from '../donate-btn/donate-btn.component';
import { PaymentV2Service, UtilService, VariablesService } from 'src/app/shared/services';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { map, Observable, startWith } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-upi-input',
  standalone: true,
  imports: [
    CommonModule, DonateBtnComponent, ReactiveFormsModule, FormsModule, MatRadioModule, MatFormFieldModule, MatInputModule,
    MatAutocompleteModule, MatButtonModule, MatTooltipModule
  ],
  templateUrl: './upi-input.component.html',
  styleUrls: ['./upi-input.component.scss']
})
export class UpiInputComponent {

  readonly vars = inject(VariablesService);
  private readonly paymentV2 = inject(PaymentV2Service);
  private readonly util = inject(UtilService);

  @Input() recommendedVpa;
  @Input() isSip;
  @Input() cartDetails;
  @Input() disableAll;
  @Input() isOrderLoading;
  @Output() onSubmit: EventEmitter<any> = new EventEmitter();

  sipUpiMaskedValidRegex = /@(upi|ybl|axl|ibl|apl|okhdfcbank|okaxis|okicici|oksbi|ptyes|ptaxis|ptsbi|pthdfc)+$/i;
  sipUpiRegex = /^\w.+@(upi|ybl|axl|ibl|apl|okhdfcbank|okaxis|okicici|oksbi|ptyes|ptaxis|ptsbi|pthdfc)+$/i;
  upiMaskedValidRegex = /@\w{2,}$/
  emailUpiControl = new FormControl('', [Validators.required, Validators.pattern(/@.{2,}/)]);
  phoneUpiControl = new FormControl('', [Validators.required, Validators.pattern(/@.{2,}/)]);
  customUpiControl = new FormControl('', [Validators.required, Validators.pattern(this.vars.upi_regex)]);
  selectedOption = new FormControl('lastused')
  upiForm = new FormGroup({
    emailUpi: this.emailUpiControl,
    phoneUpi: this.phoneUpiControl,
    customUpi: this.customUpiControl,
    selectedOption: this.selectedOption
  });

  userData = this.util.getUserData();

  upiOptions = this.vars.upiOptions;
  emailUpiOptions: Observable<string[]>;
  phoneUpiOptions: Observable<string[]>;
  customUpiOptions: Observable<string[]>;

  ngOnChanges(change: SimpleChanges) {
    if (change?.isOrderLoading?.currentValue !== change?.isOrderLoading?.previousValue && !this.disableAll) {
      this.disableFields(change?.isOrderLoading?.currentValue);
    }
    if (change?.disableAll?.currentValue !== change?.disableAll?.previousValue && !this.isOrderLoading) {
      this.disableFields(change?.disableAll?.currentValue);
    }
    if (change?.recommendedVpa?.currentValue && change?.recommendedVpa?.currentValue !== change?.disabrecommendedVpaleAll?.previousValue) {
      this.selectedOption.setValue('lastused');
    }
  }

  ngOnInit() {
    if (!this.cartDetails && this.userData) {
      this.cartDetails = { donor_email: this.userData?.email || '', donor_phone: this.userData?.phone_1 || '' }
    }
    if (!this.recommendedVpa) {
      this.selectedOption.setValue('email');
      setTimeout(() => {
        this.changeOption();
      }, 10);
    }
    if (this.isSip) {
      this.upiOptions = this.vars.sipUpiOptions;
      this.emailUpiControl.clearValidators();
      this.emailUpiControl.addValidators([Validators.required, Validators.pattern(this.sipUpiMaskedValidRegex)]);
      this.emailUpiControl.updateValueAndValidity();
      this.phoneUpiControl.clearValidators();
      this.phoneUpiControl.addValidators([Validators.required, Validators.pattern(this.sipUpiMaskedValidRegex)]);
      this.phoneUpiControl.updateValueAndValidity();
      this.customUpiControl.clearValidators();
      this.customUpiControl.addValidators([Validators.required, Validators.pattern(this.sipUpiRegex)]);
      this.customUpiControl.updateValueAndValidity();
      this.paymentV2.pledgeBtn.subscribe(tab => {
        if (tab === 'upi') {
          this.submit()
        }
      });
    }
    this.emailUpiOptions = this.emailUpiControl.valueChanges.pipe(
      startWith(''),
      map(value => this.upiOptions.filter(option => option.includes(value.toLowerCase().split('@')[1])))
    );
    this.phoneUpiOptions = this.phoneUpiControl.valueChanges.pipe(
      startWith(''),
      map(value => this.upiOptions.filter(option => option.includes(value.toLowerCase().split('@')[1])))
    );
    this.customUpiOptions = this.customUpiControl.valueChanges.pipe(
      startWith(''),
      map(value => this.upiOptions.filter(option => option.includes(value.toLowerCase().split('@')[1])))
    );
    this.emailUpiControl.disable();
    this.phoneUpiControl.disable();
    this.customUpiControl.disable();
  }

  changeOption() {
    const val = this.selectedOption.value;
    if (val === 'email') {
      this.emailUpiControl.enable();
      this.phoneUpiControl.disable();
      this.customUpiControl.disable();
    } else if (val === 'phone') {
      this.phoneUpiControl.enable();
      this.emailUpiControl.disable();
      this.customUpiControl.disable();
    } else if (val === 'custom') {
      this.customUpiControl.enable();
      this.emailUpiControl.disable();
      this.phoneUpiControl.disable();
    } else {
      this.emailUpiControl.disable();
      this.phoneUpiControl.disable();
      this.customUpiControl.disable();
    }
  }

  verifyUpi(value): boolean {
    this.emailUpiControl.updateValueAndValidity();
    this.phoneUpiControl.updateValueAndValidity();
    this.customUpiControl.updateValueAndValidity();
    return true;
  }

  disableFields(disable: boolean) {
    if (disable) {
      this.selectedOption.disable();
      this.emailUpiControl.disable();
      this.phoneUpiControl.disable();
      this.customUpiControl.disable();
    } else {
      this.selectedOption.enable();
      this.changeOption();
    }
  }

  submit() {
    const val = this.selectedOption.value;
    console.log(val);
    let upi = '';
    if (val === 'email') {
      upi = this.userData.email.split('@')[0] + this.emailUpiControl.value;
    } else if (val === 'phone') {
      upi = this.cartDetails?.donor_phone + this.phoneUpiControl.value;
    } else if (val === 'custom') {
      upi = this.customUpiControl.value;
    } else {
      upi = this.recommendedVpa;
    }
    let isValid = false;
    if (val === 'custom' && !this.isSip) {
      isValid = this.vars.upi_regex.test(upi);
    } else if (val === 'custom' && this.isSip) {
      isValid = this.sipUpiRegex.test(upi);
    } else if (this.isSip) {
      isValid = this.sipUpiMaskedValidRegex.test(upi);
    } else {
      isValid = this.upiMaskedValidRegex.test(upi);
    }
    if (!isValid) {
      this.emailUpiControl.markAsTouched();
      this.phoneUpiControl.markAsTouched();
      this.customUpiControl.markAsTouched();
      return;
    }
    // this.enableDisableAll(false);
    this.onSubmit.emit({ upi: upi, vpa_type: val });
  }
}
